import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Lock, Eye, EyeOff, Shield, AlertCircle, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const checkPasswordStrength = async (password) => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('password', password);
      
      const response = await axios.post(`${API}/auth/password-strength`, formData);
      setPasswordStrength(response.data);
    } catch (error) {
      // Password strength check failed silently
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, new_password: value });
    checkPasswordStrength(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: formData.current_password,
          new_password: formData.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'strong': return 'text-green-600';
      default: return 'text-gray-500';
    }
  };

  const getStrengthBgColor = (strength) => {
    switch (strength) {
      case 'weak': return 'bg-red-100';
      case 'medium': return 'bg-yellow-100';
      case 'strong': return 'bg-green-100';
      default: return 'bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="change-password-modal">
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <h2 className="font-manrope font-bold text-2xl text-text-primary">Change Password</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="btn-close-change-password">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                required
                value={formData.current_password}
                onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary pr-12"
                data-testid="input-current-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                required
                value={formData.new_password}
                onChange={handleNewPasswordChange}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary pr-12"
                data-testid="input-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {passwordStrength && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-sm font-medium ${getStrengthColor(passwordStrength.strength)}`}>
                    Password Strength: {passwordStrength.strength.toUpperCase()}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStrengthBgColor(passwordStrength.strength)} ${getStrengthColor(passwordStrength.strength)}`}>
                    {passwordStrength.score}/{passwordStrength.max_score}
                  </span>
                </div>
                {passwordStrength.feedback.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-text-secondary mb-1">Suggestions:</p>
                    <ul className="text-xs text-text-muted space-y-1">
                      {passwordStrength.feedback.map((item) => (
                        <li key={item}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                required
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary pr-12"
                data-testid="input-confirm-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.confirm_password && formData.new_password !== formData.confirm_password && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                Passwords do not match
              </p>
            )}
            {formData.confirm_password && formData.new_password === formData.confirm_password && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle size={12} />
                Passwords match
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading || !passwordStrength || passwordStrength.strength === 'weak'}
              className="btn-primary flex-1"
              data-testid="btn-submit-change-password"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
            <button type="button" onClick={onClose} className="btn-outline flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}